import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import { useI18n } from '../i18n'
import { Turnstile } from '../components/Turnstile'
import { config } from '../config'
import { authErrorMessage } from '../lib/errors'

type Mode = 'signin' | 'signup' | 'confirm' | 'forgot' | 'reset'

export function Auth({ initial = 'signin' }: { initial?: Mode }) {
  const { t } = useI18n()
  const auth = useAuth()
  const nav = useNavigate()
  const location = useLocation() as { state?: { from?: string }; search?: string }
  const dest = location.state?.from || '/app'

  const [mode, setMode] = useState<Mode>(initial)
  // No silent default: a generic /signup forces an explicit choice (a yard owner
  // once signed up as a horse owner without noticing the picker). The landing
  // doors preset it via ?role=… because there the choice was already made.
  const preset = new URLSearchParams(location.search || '').get('role')
  const [role, setRole] = useState<'horse' | 'farm' | null>(
    preset === 'farm' ? 'farm' : preset === 'horse' ? 'horse' : null,
  )
  // New sign-ups go to the matching onboarding; existing sign-ins honour `dest`.
  const afterSignup = role === 'farm' ? '/farm/onboarding' : '/onboarding'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [token, setToken] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [magicSent, setMagicSent] = useState(false)

  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    setErr('')
    try {
      await fn()
    } catch (e) {
      // Never surface raw Cognito errors to a non-technical user.
      setErr(authErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  const onSignIn = () => run(async () => { await auth.signIn(email, password); nav(dest, { replace: true }) })
  const onMagic = () =>
    run(async () => {
      if (!email.trim()) { setErr(t('auth.magic.needEmail')); return }
      await auth.requestMagicLink(email.trim())
      setMagicSent(true)
    })
  const onSignUp = () =>
    run(async () => {
      const { needsConfirm } = await auth.signUp(email, password, token || undefined)
      if (needsConfirm) setMode('confirm')
      else { await auth.signIn(email, password); nav(afterSignup, { replace: true }) }
    })
  const onConfirm = () =>
    run(async () => {
      await auth.confirmSignUp(email, code)
      await auth.signIn(email, password)
      nav(afterSignup, { replace: true })
    })
  const onForgot = () => run(async () => { await auth.forgotPassword(email); setMode('reset') })
  const onReset = () =>
    run(async () => { await auth.confirmForgotPassword(email, code, password); setMode('signin'); setErr('') })

  const title = t(`auth.${mode}.title`)
  // Sign-up subtitle tracks the role choice (the old static "trial" line only
  // ever applied to yards): ask first, then confirm what each side gets.
  const subtitle =
    mode === 'signup'
      ? role === null
        ? t('auth.chooseRole')
        : role === 'farm'
          ? t('auth.subtitleFarm')
          : t('auth.subtitleHorse')
      : t(`auth.${mode}.subtitle`)

  return (
    <main className="mx-auto flex min-h-[80vh] max-w-md flex-col justify-center px-5 py-10">
      <div className="card p-8">
        <h1 className="text-2xl font-extrabold tracking-tight">{title}</h1>
        <p className="mt-1 text-sm text-brand-600 dark:text-brand-300">{subtitle}</p>

        <div className="mt-6 space-y-3">
          {mode === 'signup' && (
            <div className="space-y-2">
              {(['horse', 'farm'] as const).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRole(r)}
                  aria-pressed={role === r}
                  className={`w-full rounded-xl border-2 px-4 py-3 text-left transition ${
                    role === r
                      ? 'border-brand-500 bg-brand-100/70 dark:bg-brand-800'
                      : 'border-brand-200 dark:border-brand-700 hover:border-brand-400'
                  }`}
                >
                  <span className="flex items-center justify-between gap-2 text-sm font-semibold">
                    <span>
                      {r === 'horse' ? '🐎 ' : '🏡 '}
                      {t(r === 'horse' ? 'landing.twoDoor.horseTitle' : 'landing.twoDoor.farmTitle')}
                    </span>
                    {role === r && <span className="shrink-0 text-brand-600 dark:text-brand-300">✓</span>}
                  </span>
                  <span className="mt-0.5 block text-xs leading-relaxed text-brand-600 dark:text-brand-300">
                    {t(r === 'horse' ? 'auth.roleHorseDesc' : 'auth.roleFarmDesc')}
                  </span>
                </button>
              ))}
            </div>
          )}
          {(mode === 'signin' || mode === 'signup' || mode === 'forgot') && (
            <Field label={t('auth.email')} type="email" value={email} onChange={setEmail} autoFocus />
          )}
          {(mode === 'confirm' || mode === 'reset') && (
            <Field label={t('auth.code')} value={code} onChange={setCode} autoFocus />
          )}
          {(mode === 'signin' || mode === 'signup' || mode === 'reset') && (
            <Field
              label={mode === 'reset' ? t('auth.newPassword') : t('auth.password')}
              type="password"
              value={password}
              onChange={setPassword}
            />
          )}
          {mode === 'signup' && (
            <p className="text-xs text-brand-500 dark:text-brand-400">{t('auth.passwordHint')}</p>
          )}
          {mode === 'signup' && (
            <Turnstile sitekey={config.turnstileSiteKey} onVerify={setToken} onExpire={() => setToken('')} />
          )}

          {err && <p className="rounded-lg bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">{err}</p>}

          <button
            disabled={busy || (mode === 'signup' && role === null)}
            onClick={
              mode === 'signin' ? onSignIn
              : mode === 'signup' ? onSignUp
              : mode === 'confirm' ? onConfirm
              : mode === 'forgot' ? onForgot
              : onReset
            }
            className="btn-primary w-full disabled:opacity-60"
          >
            {busy ? `${t(`auth.${mode}.cta`)}…` : t(`auth.${mode}.cta`)}
          </button>

          {mode === 'signin' && (
            magicSent ? (
              <p className="rounded-lg bg-emerald-50 dark:bg-emerald-900/30 px-3 py-2 text-center text-sm text-emerald-800 dark:text-emerald-200">
                {t('auth.magic.sent')}
              </p>
            ) : (
              <>
                <div className="flex items-center gap-3 text-xs text-brand-400">
                  <span className="h-px flex-1 bg-brand-200 dark:bg-brand-700" />
                  {t('auth.magic.or')}
                  <span className="h-px flex-1 bg-brand-200 dark:bg-brand-700" />
                </div>
                <button onClick={onMagic} disabled={busy} className="btn-ghost w-full disabled:opacity-60">
                  ✉️ {t('auth.magic.cta')}
                </button>
              </>
            )
          )}

          {mode === 'signup' && (
            <p className="text-center text-xs text-brand-500 dark:text-brand-400">
              {t('auth.agreePre')}{' '}
              <Link to="/terms" className="underline hover:text-brand-700 dark:hover:text-brand-200">{t('auth.agreeTerms')}</Link>{' '}
              {t('auth.agreeAnd')}{' '}
              <Link to="/privacy" className="underline hover:text-brand-700 dark:hover:text-brand-200">{t('auth.agreePrivacy')}</Link>
              {t('auth.agreePost')}
            </p>
          )}
        </div>

        <div className="mt-6 space-y-1 text-center text-sm text-brand-600 dark:text-brand-300">
          {mode === 'signin' && (
            <>
              <p>
                {t('auth.noAccount')}{' '}
                <button className="font-semibold text-brand-700 dark:text-brand-200 hover:underline" onClick={() => { setMode('signup'); setErr('') }}>
                  {t('auth.signup.cta')}
                </button>
              </p>
              <button className="hover:underline" onClick={() => { setMode('forgot'); setErr('') }}>{t('auth.forgotLink')}</button>
            </>
          )}
          {mode === 'signup' && (
            <p>
              {t('auth.haveAccount')}{' '}
              <button className="font-semibold text-brand-700 dark:text-brand-200 hover:underline" onClick={() => { setMode('signin'); setErr('') }}>
                {t('auth.signin.cta')}
              </button>
            </p>
          )}
          {mode === 'confirm' && (
            <button className="hover:underline" onClick={() => run(() => auth.resendCode(email))}>{t('auth.resend')}</button>
          )}
          {(mode === 'forgot' || mode === 'reset') && (
            <button className="hover:underline" onClick={() => { setMode('signin'); setErr('') }}>{t('auth.backToSignin')}</button>
          )}
        </div>
      </div>

      <p className="mt-6 text-center text-sm">
        <Link to="/" className="text-brand-500 hover:underline dark:text-brand-400">← {t('auth.backHome')}</Link>
      </p>
    </main>
  )
}

function Field({
  label, value, onChange, type = 'text', autoFocus,
}: { label: string; value: string; onChange: (v: string) => void; type?: string; autoFocus?: boolean }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-brand-700 dark:text-brand-200">{label}</span>
      <input
        type={type}
        value={value}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-brand-200 dark:border-brand-700 bg-white dark:bg-brand-900 px-4 py-2.5 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-300/40"
      />
    </label>
  )
}
